import React, { useState, useEffect, useMemo, useCallback } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  useToast,
} from '@chakra-ui/react';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import apiClient from '../lib/api/apiClient';
import { STRIPE_KEY } from '../constants';
import { useShopInventory, StoreItem } from '../hooks/useShopInventory';
import { useTeam } from '../hooks/useTeam';
import { useUserContext } from '../contexts/UserContext';

// Helper function to get payment modal description based on transaction type
const getPaymentModalDescription = (
  payloadType: string | undefined,
  upgradeDescription: string | undefined,
  excessInMillions: number,
  displayAmount: number
): string => {
  const excessText = `$${excessInMillions.toFixed(2)}M`;
  const paymentText = `$${displayAmount.toFixed(2)}`;

  if (!upgradeDescription) {
    // Fallback if no upgrade description provided
    if (payloadType === 'free_agent_offer_salary_cap_exceedance') {
      return `Your Free Agent bid would exceed your salary cap by ${excessText}.`;
    } else if (payloadType === 'waiver_claim_salary_cap_exceedance') {
      return `Your Waiver Claim would exceed your salary cap by ${excessText}.`;
    }
    return `This trade, should it be approved, will exceed your salary cap by ${excessText}.`;
  }

  // Extract the part before "To proceed" from upgradeDescription
  const beforeProceed = upgradeDescription.split(' To proceed')[0];

  if (payloadType === 'free_agent_offer_salary_cap_exceedance') {
    return `${beforeProceed} To continue, please authorize a payment of up to ${paymentText} which will be charged if you successfully sign the Free Agent. Other pending transactions may affect whether or not your card is charged for this specific transaction.`;
  } else if (payloadType === 'waiver_claim_salary_cap_exceedance') {
    return `${beforeProceed} To continue, please authorize a payment of up to ${paymentText} which will be charged if you win the Waiver Claim. Other pending transactions may affect whether or not your card is charged for this specific transaction.`;
  }

  // Default to trade message
  return `${beforeProceed} To continue, please authorize a payment of up to ${paymentText} which will be charged at the time the trade is executed. Other pending transactions may affect whether or not your card is charged for the Salary Cap increase for this specific transaction.`;
};

// Helper function to get CC auth message (shown before payment intent is created)
const getAuthMessage = (
  payloadType: string | undefined,
  upgradeDescription: string | undefined,
  excessInMillions: number
): string => {
  const excessText = `$${excessInMillions.toFixed(2)}M`;

  if (!upgradeDescription) {
    if (payloadType === 'free_agent_offer_salary_cap_exceedance') {
      return `Your Free Agent bid would exceed your salary cap by ${excessText}.`;
    } else if (payloadType === 'waiver_claim_salary_cap_exceedance') {
      return `Your Waiver Claim would exceed your salary cap by ${excessText}.`;
    }
    return `This trade would exceed your salary cap by ${excessText}.`;
  }

  // Extract the part before "To proceed" from upgradeDescription
  const beforeProceed = upgradeDescription.split(' To proceed')[0];

  if (payloadType === 'free_agent_offer_salary_cap_exceedance') {
    return `${beforeProceed} To continue, please authorize a payment for the required salary cap upgrade that will be debited from your account if you successfully sign the Free Agent.`;
  } else if (payloadType === 'waiver_claim_salary_cap_exceedance') {
    return `${beforeProceed} To continue, please authorize a payment for the required salary cap upgrade that will be debited from your account if you win the Waiver Claim.`;
  }

  // Default to trade message
  return `${beforeProceed} To continue with this trade, please authorize a payment for the required salary cap upgrade that will be debited from your account when the trade is completed.`;
};

// Helper function to get description for calculateRequiredUpgrade
const getUpgradeDescription = (
  payloadType: string | undefined,
  excessInMillions: number,
  finalNewCapValue: number
): string => {
  const excessText = `$${excessInMillions.toFixed(2)}M`;
  const capText = `$${finalNewCapValue}M`;

  if (payloadType === 'free_agent_offer_salary_cap_exceedance') {
    return `Your Free Agent bid would exceed your salary cap by ${excessText}. To proceed, you need to upgrade your salary cap to ${capText}.`;
  } else if (payloadType === 'waiver_claim_salary_cap_exceedance') {
    return `Your Waiver Claim would exceed your salary cap by ${excessText}. To proceed, you need to upgrade your salary cap to ${capText}.`;
  }

  // Default to trade message
  return `This trade would exceed your salary cap by ${excessText}. To proceed, you need to upgrade your salary cap to ${capText}.`;
};

const StripePaymentFormContent: React.FC<{
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  upgradeAmount?: number;
  upgradeDescription?: string;
  payloadType?: string;
  onSuccess: (paymentIntent: any) => void;
  onClose: () => void;
}> = ({ clientSecret, paymentIntentId, amount, upgradeAmount, upgradeDescription, payloadType, onSuccess, onClose }) => {
  // Always call hooks (they'll return null if Elements isn't rendered)
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Use upgrade amount if provided (in cents, convert to dollars)
  // CRITICAL: Never use raw 'amount' prop as fallback - it's the excess salary cap, not payment amount
  // If upgradeAmount is not provided, something went wrong - show error instead of using wrong amount
  const displayAmount = upgradeAmount ? upgradeAmount / 100 : null;
  const excessInMillions = amount / 1000000;

  // Get payment modal description based on transaction type
  const paymentModalDescription = displayAmount !== null
    ? getPaymentModalDescription(payloadType, upgradeDescription, excessInMillions, displayAmount)
    : `This trade, should it be approved, will exceed your salary cap by $${excessInMillions.toFixed(2)}M.`;

  // Safety check: If no valid display amount, show error
  if (displayAmount === null || displayAmount <= 0 || displayAmount > 10000) {
    return (
      <VStack spacing={4} align="stretch">
        <Box
          p={4}
          bg="red.900"
          color="white"
          borderRadius="md"
          border="1px solid"
          borderColor="red.500"
        >
          <Text fontSize="md" fontWeight="bold" mb={2}>
            Payment Amount Error
          </Text>
          <Text fontSize="sm">
            Unable to determine the correct payment amount. Please close this window and contact support before proceeding.
          </Text>
        </Box>
        <Button onClick={onClose} colorScheme="red" width="100%">
          Close
        </Button>
      </VStack>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      // Authorize the pre-approval with Stripe
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'An error occurred');
        setIsProcessing(false);
        return;
      }

      // Confirm the payment intent for pre-approval
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Authorization failed');
        toast({
          title: 'Authorization Error',
          description: error.message || 'Failed to authorize pre-approval',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (paymentIntent && (paymentIntent.status === 'requires_capture' || paymentIntent.status === 'succeeded')) {
        // Pre-approval authorized successfully
        // For manual capture intents, status will be 'requires_capture'
        // The payment will be captured later automatically by the system when criteria are met
        toast({
          title: 'Authorization Successful',
          description: 'Payment method authorized. The amount will be debited automatically when criteria are met.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Call onSuccess first - let the parent component handle closing the modal
        // Include paymentIntentId in the callback for linking with deals
        // Prefer paymentIntent.id from Stripe response, fallback to prop
        onSuccess({
          paymentIntent,
          paymentIntentId: paymentIntent.id || paymentIntentId
        });
        // Don't call onClose here - let the parent component decide when to close
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        toast({
          title: 'Action Required',
          description: 'Please complete the additional authentication step',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      console.error('Authorization error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      toast({
        title: 'Error',
        description: err.message || 'Failed to authorize pre-approval',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Box
          p={4}
          bg="whiteAlpha.50"
          borderRadius="md"
          border="1px solid"
          borderColor="whiteAlpha.200"
        >
          <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
            {paymentModalDescription}
          </Text>
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </Box>

        {errorMessage && (
          <Box
            p={3}
            bg="red.500"
            color="white"
            borderRadius="md"
            fontSize="sm"
          >
            {errorMessage}
          </Box>
        )}

        <Button
          type="submit"
          colorScheme="blue"
          width="100%"
          isLoading={isProcessing}
          loadingText="Authorizing..."
          isDisabled={!stripe || !elements}
        >
          Authorize ${displayAmount.toFixed(2)}
        </Button>
      </VStack>
    </form>
  );
};

interface StripePaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  payload?: Record<string, any>;
  onSuccess: (paymentIntent: any) => void;
  stripePublishableKey?: string;
}

// Default Stripe publishable key - should be overridden via props or env
const DEFAULT_STRIPE_KEY = STRIPE_KEY;

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  isOpen,
  onClose,
  amount,
  payload = {},
  onSuccess,
  stripePublishableKey,
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intentData, setIntentData] = useState<{ clientSecret: string; paymentIntentId: string } | null>(null);
  const [calculatedUpgradeAmount, setCalculatedUpgradeAmount] = useState<number | null>(null);
  const [upgradeDescription, setUpgradeDescription] = useState<string>('');

  // Get team and league info from payload
  const { myTeam, currentLeague } = useUserContext();
  const teamId = payload?.teamId || myTeam;
  const leagueId = payload?.leagueId || currentLeague?.league_id;

  // Get current team data
  const { team: currentTeamData } = useTeam(teamId || undefined);

  // Get shop inventory to find salary cap items
  const { salaryCapItems, loading: inventoryLoading } = useShopInventory({
    leagueId: leagueId || undefined,
    teamId: teamId || undefined,
    activeOnly: true,
  });

  // Helper function to extract level from a salary cap item
  const getItemLevel = (item: StoreItem): number | null => {
    const raw = item.raw as any;
    const level = raw?.item?.metadata?.level;
    return level !== null && level !== undefined ? Number(level) : null;
  };

  // Helper function to extract new_cap_value from item (in millions)
  const getItemNewCapValue = (item: StoreItem): number | null => {
    const raw = item.raw as any;
    const newCapValue = raw?.item?.metadata?.new_cap_value ?? raw?.metadata?.new_cap_value ?? null;
    return newCapValue !== null && newCapValue !== undefined ? Number(newCapValue) : null;
  };

  // Helper function to extract salary cap increase amount from item (in millions)
  const getItemIncrease = (item: StoreItem): number => {
    const raw = item.raw as any;
    const increase = raw?.item?.metadata?.salary_cap_increase ??
      raw?.item?.metadata?.salaryCapIncrease ??
      raw?.metadata?.salary_cap_increase ??
      raw?.metadata?.salaryCapIncrease ??
      null;
    return increase !== null && increase !== undefined ? Number(increase) : 0;
  };

  // Calculate required salary cap upgrade
  const calculateRequiredUpgrade = useMemo(() => {
    if (!currentTeamData?.salary_cap || !amount || !teamId || inventoryLoading || salaryCapItems.length === 0) {
      return { upgradeAmount: null, description: '', newCapValue: null, salaryCapLevel: null };
    }

    // Current salary cap is in millions (e.g., 175 = 175M)
    const currentSalaryCap = Number(currentTeamData.salary_cap);

    // Excess amount is in dollars, convert to millions
    const excessInMillions = amount / 1000000;

    // Required new salary cap in millions
    const requiredNewCap = currentSalaryCap + excessInMillions;

    // Get current salary cap level
    const currentSalaryCapLevel = (currentTeamData as any)?.salary_cap_level || 0;

    // Filter and sort salary cap items
    const availableSalaryCapItems = salaryCapItems
      .filter((item) => {
        const itemLevel = getItemLevel(item);
        if (itemLevel === null) return false;
        return itemLevel > currentSalaryCapLevel;
      })
      .sort((a, b) => {
        const levelA = getItemLevel(a) ?? 0;
        const levelB = getItemLevel(b) ?? 0;
        return levelA - levelB;
      });

    if (availableSalaryCapItems.length === 0) {
      return { upgradeAmount: null, description: 'No salary cap upgrades available.', newCapValue: null, salaryCapLevel: null };
    }

    // Find the smallest item that would cover the required cap
    let selectedItem: StoreItem | null = null;
    for (const item of availableSalaryCapItems) {
      const newCapValue = getItemNewCapValue(item);
      if (newCapValue !== null && newCapValue >= requiredNewCap) {
        selectedItem = item;
        break;
      }
    }

    // If no single item covers it, use the highest available item
    if (!selectedItem) {
      selectedItem = availableSalaryCapItems[availableSalaryCapItems.length - 1];
    }

    if (!selectedItem) {
      return { upgradeAmount: null, description: 'Unable to find suitable salary cap upgrade.', newCapValue: null, salaryCapLevel: null };
    }

    // Get all items from first available level up to and including selected level
    const selectedLevel = getItemLevel(selectedItem);
    const includedItems = selectedLevel !== null
      ? availableSalaryCapItems.filter((item) => {
        const itemLevel = getItemLevel(item);
        return itemLevel !== null && itemLevel <= selectedLevel;
      })
      : [selectedItem];

    // Calculate cumulative price
    const cumulativePrice = includedItems.reduce((sum, item) => sum + item.price, 0);

    // Calculate cumulative increase
    const cumulativeIncrease = includedItems.reduce((sum, item) => sum + getItemIncrease(item), 0);

    // Get the new cap value for display
    const finalNewCapValue = getItemNewCapValue(selectedItem) || (currentSalaryCap + cumulativeIncrease);

    const description = getUpgradeDescription(payload?.type, excessInMillions, finalNewCapValue);

    return {
      upgradeAmount: Math.round(cumulativePrice * 100),
      description,
      newCapValue: finalNewCapValue,
      salaryCapLevel: selectedLevel,
    };
  }, [currentTeamData, amount, teamId, salaryCapItems, inventoryLoading]);

  // Update calculated upgrade amount when calculation changes
  useEffect(() => {
    if (calculateRequiredUpgrade.upgradeAmount !== null) {
      setCalculatedUpgradeAmount(calculateRequiredUpgrade.upgradeAmount);
      setUpgradeDescription(calculateRequiredUpgrade.description);
    }
  }, [calculateRequiredUpgrade]);

  // Load Stripe library when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setIntentData(null);
      setError(null);
      setCalculatedUpgradeAmount(null);
      setUpgradeDescription('');
      return;
    }

    // Load Stripe publishable key
    const loadStripeKey = async () => {
      let publishableKey = DEFAULT_STRIPE_KEY;

      if (!publishableKey) {
        try {
          const keyResponse = await apiClient.get('/store/stripe-publishable-key');
          publishableKey = keyResponse.data?.publishableKey || keyResponse.data?.key || '';
        } catch (keyError) {
          console.warn('Could not fetch Stripe publishable key from API');
        }
      }

      if (publishableKey) {
        setStripePromise(loadStripe(publishableKey));
        setLoading(false);
      } else {
        // Fallback to STRIPE_KEY from constants
        if (STRIPE_KEY) {
          setStripePromise(loadStripe(STRIPE_KEY));
          setLoading(false);
        } else {
          console.error('Stripe publishable key not found.');
          setError('Stripe configuration error.');
          setLoading(false);
        }
      }
    };

    loadStripeKey();
  }, [isOpen, stripePublishableKey]);

  // Function to create payment intent - memoized to avoid unnecessary re-renders
  const createPaymentIntent = useCallback(async () => {
    // Don't create if already created
    if (intentData) {
      return;
    }

    // Wait for upgrade calculation if we're calculating it (for trade salary cap exceedance)
    const isTradeExceedance = payload?.type === 'trade_salary_cap_exceedance';
    if (isTradeExceedance && calculatedUpgradeAmount === null && inventoryLoading) {
      // Still calculating, wait for it
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // CRITICAL: Never use the raw 'amount' prop as payment amount - it represents the excess salary cap
      // (which can be millions of dollars). Only use the calculated upgrade amount.
      // For trade salary cap exceedance, we MUST have a calculated upgrade amount
      let finalAmount: number | null = null;

      if (isTradeExceedance) {
        // For trade exceedance, we MUST use the calculated upgrade amount
        if (calculatedUpgradeAmount === null && calculateRequiredUpgrade.upgradeAmount === null) {
          setError('Unable to calculate upgrade amount. Please try again or contact support.');
          setLoading(false);
          return;
        }
        finalAmount = calculatedUpgradeAmount ?? calculateRequiredUpgrade.upgradeAmount;
      } else {
        // For other types (waiver_claim, free_agent_offer), try to use calculated amount if available
        // Check both state and memoized value to handle race conditions when modal reopens
        // Otherwise, we may need to handle them differently - but never use raw 'amount'
        if (calculatedUpgradeAmount !== null) {
          finalAmount = calculatedUpgradeAmount;
        } else if (calculateRequiredUpgrade.upgradeAmount !== null) {
          // Fallback to memoized value if state hasn't updated yet (handles modal reopen race condition)
          finalAmount = calculateRequiredUpgrade.upgradeAmount;
        } else {
          // If no calculated amount for non-trade types, we shouldn't proceed without validation
          setError('Unable to determine payment amount. Please try again or contact support.');
          setLoading(false);
          return;
        }
      }

      // Safety check: Ensure finalAmount is reasonable (not excessive)
      // Reject any amount that seems like it could be the salary cap difference instead of upgrade cost
      // Upgrade costs should be reasonable (e.g., under $10,000 = 1,000,000 cents)
      // If amount is over 1 million cents ($10,000), it's likely an error
      if (finalAmount === null || finalAmount <= 0) {
        setError('Invalid payment amount. Please try again or contact support.');
        setLoading(false);
        return;
      }

      // Additional safety: If amount is suspiciously large (over $10,000), log warning and reject
      // The 'amount' prop (excess salary cap) can be millions, so if finalAmount is close to it, it's wrong
      if (finalAmount > 1000000) { // $10,000
        console.error('CRITICAL: Payment amount is suspiciously large:', {
          finalAmount,
          originalAmount: amount,
          calculatedUpgradeAmount,
          payloadType: payload?.type,
        });
        setError('Payment amount calculation error detected. Please contact support before proceeding.');
        setLoading(false);
        return;
      }

      // Build enhanced payload with salary cap metadata
      const enhancedPayload = { ...payload };

      // Add salary cap metadata if this is a trade salary cap exceedance
      // Stripe metadata values must be strings, so we flatten the object into separate string fields
      if (payload?.type === 'trade_salary_cap_exceedance' && calculateRequiredUpgrade.newCapValue !== null) {
        // Calculate new total payroll: current salary cap (in dollars) + excess amount
        const currentSalaryCapInDollars = currentTeamData?.salary_cap ? Number(currentTeamData.salary_cap) * 1000000 : 0;
        const newTotalPayroll = currentSalaryCapInDollars + amount;

        // Add salary cap metadata as flattened string fields (Stripe requires all metadata values to be strings)
        enhancedPayload.salary_cap_increase_new_cap_value = String(calculateRequiredUpgrade.newCapValue); // in millions
        enhancedPayload.salary_cap_increase_new_total_payroll = String(newTotalPayroll); // in dollars
        enhancedPayload.salary_cap_increase_salary_cap_level = String(calculateRequiredUpgrade.salaryCapLevel);
        enhancedPayload.description_en = `Payment authorization required to increase salary cap from ${currentTeamData?.salary_cap}M to ${calculateRequiredUpgrade.newCapValue}M.`;
      }
      // Create the PaymentIntent on backend
      const response = await apiClient.post('/store/create-pre-approval-intent', {
        amount: finalAmount,
        payload: enhancedPayload,
      });
      console.log('response', response);

      const preApprovalIntent = response.data.preApprovalIntent?.preApprovalIntent || response.data.preApprovalIntent;

      setIntentData({
        clientSecret: preApprovalIntent.client_secret,
        paymentIntentId: preApprovalIntent.id || preApprovalIntent.payment_intent_id,
      });
    } catch (e: any) {
      console.error('Error creating payment intent:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to create payment intent. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [intentData, payload, calculatedUpgradeAmount, inventoryLoading, amount, calculateRequiredUpgrade, currentTeamData]);

  // Don't automatically create payment intent - wait for user to click "Start Authorization"

  // Update rendering logic
  if (!isOpen) return null;

  // If Stripe library is still loading
  if (!stripePromise) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Pre-Approve Funds</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text>{error || 'Loading Stripe...'}</Text>
              <Text fontSize="sm" color="whiteAlpha.600">
                If this persists, please ensure the Stripe publishable key is configured and the API is accessible.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // If intent hasn't been created yet, show button to start authorization
  if (!intentData) {
    // Wait for calculations to complete before enabling button (for all types)
    const hasValidAmount = calculatedUpgradeAmount !== null || calculateRequiredUpgrade.upgradeAmount !== null;
    const isReadyToStart = !inventoryLoading && hasValidAmount;

    // Determine the display amount for the button
    // CRITICAL: Never use raw 'amount' - it's the excess salary cap, not the payment amount
    const displayAmountForButton = calculatedUpgradeAmount !== null
      ? calculatedUpgradeAmount / 100
      : calculateRequiredUpgrade.upgradeAmount !== null
        ? calculateRequiredUpgrade.upgradeAmount / 100
        : null; // Don't fall back to 'amount' - show error or wait for calculation

    // Safety check: Don't show button if we don't have a valid display amount
    if (displayAmountForButton === null || displayAmountForButton > 10000) {
      const hasError = displayAmountForButton !== null && displayAmountForButton > 10000;
      return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Authorize Salary Cap Increase Payment</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box
                  p={4}
                  bg="whiteAlpha.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
                    {getAuthMessage(payload?.type, upgradeDescription || calculateRequiredUpgrade.description, amount / 1000000)}
                  </Text>
                </Box>

                {hasError ? (
                  <Box
                    p={3}
                    bg="red.500"
                    color="white"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    Payment amount calculation error. Please contact support.
                  </Box>
                ) : inventoryLoading ? (
                  <Text textAlign="center" color="whiteAlpha.600">
                    Calculating upgrade amount...
                  </Text>
                ) : (
                  <Text textAlign="center" color="red.400">
                    Unable to calculate upgrade amount. Please contact support.
                  </Text>
                )}
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      );
    }

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Authorize Salary Cap Increase Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box
                p={4}
                bg="whiteAlpha.50"
                borderRadius="md"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
                  {getAuthMessage(payload?.type, upgradeDescription || calculateRequiredUpgrade.description, amount / 1000000)}
                </Text>
              </Box>

              {error && (
                <Box
                  p={3}
                  bg="red.500"
                  color="white"
                  borderRadius="md"
                  fontSize="sm"
                >
                  {error}
                </Box>
              )}

              {inventoryLoading && calculatedUpgradeAmount === null && calculateRequiredUpgrade.upgradeAmount === null ? (
                <Text textAlign="center" color="whiteAlpha.600">
                  Calculating upgrade amount...
                </Text>
              ) : (
                <Button
                  colorScheme="blue"
                  width="100%"
                  onClick={createPaymentIntent}
                  isLoading={loading}
                  loadingText="Preparing payment form..."
                  isDisabled={!isReadyToStart || loading}
                >
                  Start Authorization (${displayAmountForButton.toFixed(2)})
                </Button>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Intent created, show Stripe Elements form
  const options: StripeElementsOptions = {
    clientSecret: intentData.clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#3182ce',
        colorBackground: '#1a202c',
        colorText: '#ffffff',
        colorDanger: '#e53e3e',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>CC Authorization Required to Increase Salary Cap</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Elements stripe={stripePromise} options={options}>
            <StripePaymentFormContent
              clientSecret={intentData.clientSecret}
              paymentIntentId={intentData.paymentIntentId || ''}
              amount={amount || 0}
              upgradeAmount={calculatedUpgradeAmount ?? calculateRequiredUpgrade.upgradeAmount ?? undefined}
              upgradeDescription={upgradeDescription || calculateRequiredUpgrade.description || undefined}
              payloadType={payload?.type}
              onSuccess={(result) => {
                // Ensure paymentIntentId is included in the callback
                onSuccess({
                  ...result,
                  paymentIntentId: result.paymentIntentId || intentData.paymentIntentId,
                });
              }}
              onClose={onClose}
            />
          </Elements>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default StripePaymentForm;

